const customJsonstringify = str => JSON.stringify(str).replace(/([&"'<>])/g, ( char) => {
  switch (char) {
  case '&':
      return '\u0026'
  case '<':
      return '\u003c'
  case '>': 
      return '\u003e'
  case '"':
      return '\u0022'
  case "'":
      return "\u0027"
  default:
      return char
  }
})
const axios = require('axios').default;
const queryCompress = require('graphql-query-compress');
const queries = require('../graphql-queries');
const contentfulConfig = require('../config').contentful;
const pageTypes = require('../constants/global').contentType;
const runtimeConfig = require('../runtimeConfig');

const EXPORT_RETRY_DELAY_MS = 1000;
const CONTENTFUL_REQUEST_TIMEOUT_MS =
  Number(process.env.CONTENTFUL_REQUEST_TIMEOUT_MS) || 20000;
const CONTENTFUL_EXPORT_MAX_RETRIES =
  Number(process.env.CONTENTFUL_EXPORT_MAX_RETRIES) || 2;
const CONTENTFUL_QUERY_SIZE_LIMIT = 8192;
const SECTION_QUERY_DEFAULT_BATCH_SIZE =
  Number(process.env.CONTENTFUL_SECTION_QUERY_BATCH_SIZE) || 3;
const { serverRuntimeConfig } = runtimeConfig;

export function getContentfulEndpoint() {
  const CF_ACCESS_TOKEN =
    process.env.CF_PREVIEW === 'true'
      ? serverRuntimeConfig.CONTENTFUL_PREVIEW_ACCESS_TOKEN
      : serverRuntimeConfig.CF_DELIVERY_ACCESS_TOKEN;

  return contentfulConfig.endpoint
    .replace('{spaceId}', contentfulConfig.spaceId)
    .replace('{environment}', contentfulConfig.environment || 'master')
    .replace('{accessToken}', CF_ACCESS_TOKEN);
}

function delay(delayMs) {
  return new Promise(resolve => {
    setTimeout(resolve, delayMs);
  });
}

function getResolvedSectionIds(pageContentItems = []) {
  const sectionIdsArray = [];
  pageContentItems.map(item => {
    if (item && item.sys.id) {
      sectionIdsArray.push(item.sys.id);
    }
    return item;
  });
  return sectionIdsArray;
}

export async function getContentfulData(query, endpoint, attempt = 0) {
  const compressedQuery = queryCompress(query);
  try {
    const { data } = await axios.post(
      endpoint,
      { query: compressedQuery },
      { timeout: CONTENTFUL_REQUEST_TIMEOUT_MS }
    );
    if (!data.data) {
      throw new Error('Error occured with the Contentful Query');
    }
    return data;
  } catch (e) {
    const shouldRetry =
      process.env.NEXT_EXPORT === 'true' &&
      attempt < CONTENTFUL_EXPORT_MAX_RETRIES;

    if (shouldRetry) {
      await delay(EXPORT_RETRY_DELAY_MS);
      return getContentfulData(query, endpoint, attempt + 1);
    }

    const remoteErrors = e?.response?.data?.errors;
    const detailedMessage = Array.isArray(remoteErrors)
      ? remoteErrors.map(err => err.message).join('; ')
      : e?.message || String(e);
    throw new Error(
      `Contentful query failed after ${attempt + 1} attempt(s): ${detailedMessage}`
    );
  }
}

// This function sorts our section containers in sequence given by the page it is linked to.
function sortSectionsInSequence(allSections = [], sectionIds = []) {
  const sortedSections = [];
  sectionIds.forEach(id => {
    const sectionObject = allSections.find(section => section.sys.id === id);
    sortedSections.push(sectionObject);
  });
  return sortedSections;
}

// Get sectionsforpage
// This function gets all the PageContentCollection data from the sys_ids from pages query response

export async function getPageSectionsForPage(
  endpoint,
  locale,
  pageContentCollection
) {
  return new Promise((resolve, reject) => {
    let pageContentCollectionClone = JSON.parse(
      customJsonstringify(pageContentCollection)
    );
    let sectionIdsArray = getResolvedSectionIds(
      pageContentCollectionClone.items
    );
    // Keep sectionContainer queries below Contentful's maximum query size.
    const queryLimit = SECTION_QUERY_DEFAULT_BATCH_SIZE;
    pageContentCollectionClone.items = [];
    const allSectionPromises = [];
    for (let i = 0; i < sectionIdsArray.length;) {
      let currentBatchSize = Math.min(queryLimit, sectionIdsArray.length - i);
      let query = '';
      let sectionIdsForQuery = [];

      while (currentBatchSize > 0) {
        sectionIdsForQuery = sectionIdsArray.slice(i, i + currentBatchSize);
        query = queries.sectionContainer(
          locale,
          contentfulConfig.preview,
          sectionIdsForQuery
        );

        if (queryCompress(query).length <= CONTENTFUL_QUERY_SIZE_LIMIT) {
          break;
        }

        currentBatchSize -= 1;
      }

      if (!sectionIdsForQuery.length || !query) {
        reject(new Error('Unable to build valid sectionContainer query batch'));
        return;
      }

      allSectionPromises.push(getContentfulData(query, endpoint));
      i += sectionIdsForQuery.length;
    }

    axios
      .all(allSectionPromises)
      .then(
        axios.spread((...allSectionData) => {
          allSectionData.forEach(sectionContainerData => {
            pageContentCollectionClone.items = [
              ...pageContentCollectionClone.items,
              ...sectionContainerData.data.sectionContainerCollection.items,
            ];
          });
          const sortedSections = sortSectionsInSequence(
            pageContentCollectionClone.items,
            sectionIdsArray
          );
          pageContentCollectionClone.items = sortedSections;
          resolve(pageContentCollectionClone);
        })
      )
      .catch(err => {
        reject(err);
      });
  });
}

// This function gets Contentful data based on page type
// If you also have pageContentCollection in your page, please call the getPageSectionsForPage function
// and just give the sys id in your page query for pageContentCollection
export async function getContentfulQueryData(pageType, locale, filters, isLoadExtraPDP = false) {
 const contentfulEndpoint = getContentfulEndpoint();

  let query = '';
  // Here we fetch data based on the page type given.
  switch (pageType) {
    case pageTypes.urlBuilder:
      query = queries.urlBuilder(locale, contentfulConfig.preview);
      return await getContentfulData(query, contentfulEndpoint);

    case pageTypes.layout:
      query = queries.layout(locale, contentfulConfig.preview);
      return await getContentfulData(query, contentfulEndpoint);

    case pageTypes.siteMap:
      query = queries[pageTypes.siteMap](locale, contentfulConfig.preview);
      let siteMapData = await getContentfulData(query, contentfulEndpoint);
      let formatedSiteMapData = siteMapData;
      return formatedSiteMapData;

    case pageTypes.homePage:
      query = queries[pageTypes.homePage](locale, contentfulConfig.preview);
      let homePageData = await getContentfulData(query, contentfulEndpoint);

      // This call gets the pageContentCollection separately.

      const allHomePageSectionData = await getPageSectionsForPage(
        contentfulEndpoint,
        locale,
        homePageData.data.homePageCollection.items[0].pageContentCollection
      );
      homePageData.data.homePageCollection.items[0].pageContentCollection = allHomePageSectionData;

      return homePageData;

    case pageTypes.articlePage:
      query = queries[pageTypes.articlePage](
        locale,
        contentfulConfig.preview,
        filters
      );
      let articleDetailData = await getContentfulData(
        query,
        contentfulEndpoint
      );
      const allArticleDetailSectionData = await getPageSectionsForPage(
        contentfulEndpoint,
        locale,
        articleDetailData.data.articleCollection.items[0].pageContentCollection
      );
      articleDetailData.data.articleCollection.items[0].pageContentCollection = allArticleDetailSectionData;
      return articleDetailData;

    case pageTypes.articleListPage:
      query = queries[pageTypes.articleListPage](
        locale,
        contentfulConfig.preview,
        filters
      );
      let articleListData = await getContentfulData(query, contentfulEndpoint);
      const allArticleListSectionData = await getPageSectionsForPage(
        contentfulEndpoint,
        locale,
        articleListData.data.articleListCollection.items[0]
          .pageContentCollection
      );
      articleListData.data.articleListCollection.items[0].pageContentCollection = allArticleListSectionData;
      return articleListData;

    case pageTypes.contentPage:
      query = queries[pageTypes.contentPage](
        locale,
        contentfulConfig.preview,
        filters
      );
      let contentData = await getContentfulData(query, contentfulEndpoint);
      const allContentSectionData = await getPageSectionsForPage(
        contentfulEndpoint,
        locale,
        contentData.data.contentPageCollection.items[0].pageContentCollection
      );
      contentData.data.contentPageCollection.items[0].pageContentCollection = allContentSectionData;
      return contentData;

    case pageTypes.productListPage:
      query = queries[pageTypes.productListPage](
        locale,
        contentfulConfig.preview,
        filters
      );
      let productListingData = await getContentfulData(
        query,
        contentfulEndpoint
      );
      const allProductSectionDataPLP = await getPageSectionsForPage(
        contentfulEndpoint,
        locale,
        productListingData.data.productListCollection.items[0]
          .pageContentCollection
      );
      productListingData.data.productListCollection.items[0].pageContentCollection = allProductSectionDataPLP;
      return await productListingData;
case pageTypes.newProductPage:
      query =
        queries[pageTypes.newProductPage](locale, contentfulConfig.preview, filters);
      return await getContentfulData(query, contentfulEndpoint);
    case pageTypes.productPage:
      query =
        queries[pageTypes.productPage](locale, contentfulConfig.preview, filters);
         
      return await getContentfulData(query, contentfulEndpoint);
    case pageTypes.registrationPage:
      query = queries[pageTypes.registrationPage](
        locale,
        contentfulConfig.preview
      );
      let registrationPageData = await getContentfulData(
        query,
        contentfulEndpoint
      );
      return registrationPageData;

    case pageTypes.loginPage:
      query = queries[pageTypes.loginPage](locale, contentfulConfig.preview);
      let loginPageData = await getContentfulData(query, contentfulEndpoint);
      return loginPageData;
    default:
      return null;
  }
}